/**
 * Org module entitlements for SafeUI (must match superadmin `modules.slug` / onboarding wizard).
 * Resolves modules from localStorage user (several legacy shapes) and enriched JWT payloads.
 */

/** Prefer first matching slug so post-login lands on an entitled area. */
const POST_LOGIN_RULES = [
  ['monitoring', '/ai-explorer'],
  ['compliance', '/ai-explorer'],
  ['governance', '/ai-explorer'],
  ['finops', '/finops'],
  ['security', '/security/threat-control'],
  ['copilot', '/copilot/dashboard'],
  ['copilot_studio', '/copilot/studio/dashboard'],
  ['gemini', '/gemini/dashboard'],
  // Unified SaaS-AI module — land on the vendor the org is entitled to.
  ['openai', '/saas-ai/dashboard?vendor=openai'],
  ['anthropic', '/saas-ai/dashboard?vendor=anthropic'],
  ['google_antigravity', '/saas-ai/dashboard?vendor=google_antigravity'],
];

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

/**
 * Normalize legacy shapes: SSO sometimes stored `{ user, organization }` as the whole "user" blob.
 */
export function readNormalizedUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem('user') || '{}');
    if (raw && typeof raw === 'object' && raw.user && typeof raw.user === 'object' && !Array.isArray(raw.modules)) {
      const inner = raw.user;
      const org = raw.organization || {};
      return {
        ...inner,
        organization_id: inner.organization_id ?? org.organization_id,
        organization_name: inner.organization_name ?? org.organization_name,
      };
    }
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}

export function getModuleSlugsFromJwtToken(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || !Array.isArray(payload.modules)) return null;
  return payload.modules;
}

/**
 * Effective org module slugs: JWT `modules` claim (enriched tokens), else localStorage user.modules.
 *
 * The JWT wins when present because it's re-issued on every token refresh
 * (see continueSession() in services/auth/sessionExpiry.js), while
 * localStorage.user is only rewritten at full login/SSO/impersonation
 * events. Preferring the stale user.modules meant a Super Admin toggling a
 * module for an org wasn't reflected in the app until the user fully
 * logged out and back in — checking the JWT first fixes that without
 * requiring a logout.
 */
export function getEffectiveOrgModuleSlugs() {
  const fromJwt = getModuleSlugsFromJwtToken(localStorage.getItem('jwt_token'));
  if (Array.isArray(fromJwt)) {
    return fromJwt;
  }
  const user = readNormalizedUserFromStorage();
  return Array.isArray(user.modules) ? user.modules : [];
}

export function getPostLoginPath(moduleSlugs) {
  const list = Array.isArray(moduleSlugs) ? moduleSlugs : [];
  const set = new Set(list.map((s) => String(s).trim().toLowerCase()).filter(Boolean));
  if (set.size === 0) {
    return '/ai-explorer';
  }
  for (const [slug, path] of POST_LOGIN_RULES) {
    if (set.has(slug)) return path;
  }
  return '/ai-explorer';
}

/**
 * Per-user welcome page completion flag. Stored in localStorage as a v1 signal —
 * lossy across devices/browsers, but it keeps onboarding state on the customer
 * side until we have a real backend flag. `null` userId is treated as "no
 * persistent identity" → always show welcome.
 */
function welcomeFlagKey(userId) {
  return `welcome_completed_${userId == null ? 'anon' : String(userId)}`;
}

export function isWelcomeCompleted(userId) {
  try {
    return localStorage.getItem(welcomeFlagKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function markWelcomeCompleted(userId) {
  try {
    localStorage.setItem(welcomeFlagKey(userId), '1');
  } catch {
    // ignore quota / privacy-mode errors — welcome will just show again next login
  }
}

/** Flatten `{ user, organization }` from `/profile-details` into the shape Sidebar / ProtectedRoute expect. */
export function persistUserFromProfileApiResponse(apiData) {
  const data = apiData || {};
  const inner = data.user && typeof data.user === 'object' ? data.user : data;
  const org = data.organization || {};
  const flat = {
    ...inner,
    organization_id: inner.organization_id ?? org.organization_id,
    organization_name: inner.organization_name ?? org.organization_name,
    modules: Array.isArray(inner.modules) ? inner.modules : [],
  };
  localStorage.setItem('user', JSON.stringify(flat));
  return flat;
}
